import {useCallback, useMemo} from 'react';
import {useDispatch, useSelector} from 'react-redux';
import utils from '../helpers/utils';
import {alertsSetInboxFilter, getAlertListInbox, getAlertListDarkWebInbox} from '../actions/alertList';
import {RootState} from '../reducers/rootReducer';
import {getLangCode, stringBundle} from '../stringBundle';
import {useAlertCategoryFilter} from './useAlertCategoryFilter';
import {useQuery} from '@tanstack/react-query';
import {fetchRest} from '../helpers/fetchRest';

export const creditMonitorAlertTypes = [
  'Credit Balance Change',
  'Credit Inquiry',
  'Instant Credit Inquiry',
  'New Account',
  'Tradeline',
];

// Define types for alert filter objects
interface AlertFilter {
  name: string;
  value: string;
}
interface AlertCategoryFilter {
  alertCategory: string | null;
  alertBucket: string | null;
}

/**
 * Interface defining the return values from the useAlerts hook
 */
interface UseAlertsReturnType {
  filterAlert: (_alertTypeNameKeyObject: string, _alertBucket: string) => void;
  fetchInboxAlerts: () => Promise<unknown>;
  alertCategoryFilter: AlertCategoryFilter;
  alertList: Record<string, unknown>;
}

/**
 * Custom hook to handle alert data fetching and filtering
 * @returns Alert handling methods and state
 */
export const useAlerts = (): UseAlertsReturnType => {
  const dispatch = useDispatch();
  const {filter = {}} = useAlertCategoryFilter();
  const alertCategoryFilter = useMemo(
    (): AlertCategoryFilter => ({
      alertCategory: null,
      alertBucket: null,
      ...filter,
    }),
    [filter]
  );
  const alertList = useSelector((store: RootState) => store.alertList);
  const auth = useSelector((store: RootState) => store.auth);
  const localizedStringBundle = stringBundle(getLangCode(), null);

  /**
   * This function will get the alerts category list for a particular alert bucket, if the category passed in browser
   * url params exist in the alert category list. It will filter the alerts list of the bucket based on the category.
   * @param {string} alertTypeNameKeyObject - containing the key of the alert type name key object
   * @param {string} alertBucket - containing the alert bucket name
   */
  const filterAlert = useCallback(
    (alertTypeNameKeyObject: string, alertBucket: string) => {
      const inboxDateFilter = utils.safeGet(alertList, 'inboxDateFilter');
      const allMembersFilter: AlertFilter = {
        name: utils.safeGet(localizedStringBundle, 'ALL_MEMBERS', 'All Members'),
        value: 'All',
      };
      const defaultFilter: AlertFilter = {
        name: utils.safeGet(localizedStringBundle, 'ALL_CATEGORIES', 'All Categories'),
        value: 'All',
      };
      const alertTypeObjectList = utils.safeGet(alertList, alertTypeNameKeyObject, []);
      const alertFilter =
        alertTypeObjectList.find(({name}: {name: string}) => name === alertCategoryFilter.alertCategory) ||
        defaultFilter;
      switch (alertBucket) {
        case 'inbox':
          dispatch(alertsSetInboxFilter(alertFilter, allMembersFilter, 'alerttypeDropdown', inboxDateFilter));
          break;
        default:
          break;
      }
    },
    [alertList, alertCategoryFilter, localizedStringBundle, dispatch]
  );

  /**
   * Fetches inbox alerts. Detects if this is a dark web UI and fetches the appropriate alerts.
   */
  const fetchInboxAlerts = useCallback(() => {
    const isDwm = window.isDwm || utils.safeGet(auth, 'user.primaryMember.plan.isDwm', false);
    const userCountryCode = utils.safeGet(auth, 'user.countryCode', '');

    // Use the appropriate fetch method based on isDwm
    const fetchPromise = isDwm ? dispatch(getAlertListDarkWebInbox(userCountryCode)) : dispatch(getAlertListInbox());

    return fetchPromise.then(() => {
      const {alertCategory, alertBucket} = alertCategoryFilter;
      if (alertCategory && alertBucket === 'inbox') {
        filterAlert('inboxAlertTypeNameKeysObject', alertBucket);
      }
      return Promise.resolve();
    });
  }, [alertCategoryFilter, dispatch, filterAlert, auth]);

  return {
    filterAlert,
    fetchInboxAlerts,
    alertCategoryFilter,
    alertList,
  };
};

interface AlertCountsResponse {
  total: number;
  categories?: {
    [key: string]: number;
  };
  alertTypes?: {
    'Credit Balance Change'?: number;
    'Credit Inquiry'?: number;
    'Instant Credit Inquiry'?: number;
    'New Account'?: number;
    Tradeline?: number;
  };
}

type AlertCountsParams = {
  status?: string[];
  startDateTime: string;
  endDateTime: string;
  categories?: string[];
  alertTypes?: string[];
};

/**
 * Builds the request url with necessary quer
 * @param params {AlertCountsParams} - parameters for the request
 * @returns
 */
export const fetchAlertCounts = async(params: AlertCountsParams): Promise<AlertCountsResponse> => {
  // Build query string from params
  const queryParams: string[] = [];

  if (params.status && params.status.length > 0) {
    queryParams.push(`status=${params.status.map(encodeURIComponent).join(',')}`);
  }

  if (params.categories && params.categories.length > 0) {
    queryParams.push(`categories=${params.categories.map(encodeURIComponent).join(',')}`);
  } else if (params.alertTypes && params.alertTypes.length > 0) {
    queryParams.push(`alertTypes=${params.alertTypes.map(encodeURIComponent).join(',')}`);
  }

  if (params.startDateTime) {
    queryParams.push(`startDateTime=${encodeURIComponent(params.startDateTime)}`);
  }

  if (params.endDateTime) {
    queryParams.push(`endDateTime=${encodeURIComponent(params.endDateTime)}`);
  }

  const url = `${utils.getAlertsCountsPath()}${queryParams.length > 0 ? `?${queryParams.join('&')}` : ''}`;
  return fetchRest(url, {
    method: 'GET',
    headers: {
      Accept: 'application/json',
      'Content-Type': 'application/json',
      lang_code: getLangCode(),
    },
    credentials: 'include',
  });
};

/**
 * Custom hook that fetches and manages alert counts using React Query
 *
 * This hook fetches alert count data based on provided filters by wrapping the fetchAlertCounts
 * function. It constructs the appropriate query parameters based on the input parameters.
 *
 * @param lookBackStartDate - ISO string date representing the start date filter for fetching alerts
 * @param alertStatus {['all'] | ['unread']} - Array of alert status values to filter by (e.g., ['all'], ['unread'])
 * @param alertTypes - Optional array of specific alert types to filter by. Used if alertCategories is empty.
 * @param alertCategories - Optional array of alert categories to filter by. Takes precedence over alertTypes.
 * @param lookBackEndDate - Optional ISO string date representing the end date filter for fetching alerts
 *
 * @returns React Query result object containing the alert counts data, loading state, and error state
 * with a structure matching AlertCountsResponse
 */
export const useAlertsCount = (
  lookBackStartDate: string,
  alertStatus: string[],
  alertTypes: string[] = [],
  alertCategories: string[] = [],
  lookBackEndDate?: string
) => {
  // Get current date as ISO string
  const endDate = lookBackEndDate ? lookBackEndDate : new Date().toISOString();

  const reqParams: AlertCountsParams = {
    startDateTime: lookBackStartDate,
    endDateTime: endDate,
  };

  // Use either alertTypes or alertCategories
  if (Array.isArray(alertCategories) && alertCategories.length > 0) {
    reqParams.categories = alertCategories;
  } else if (Array.isArray(alertTypes) && alertTypes.length > 0) {
    reqParams.alertTypes = alertTypes;
  }

  // Add status if provided
  if (Array.isArray(alertStatus) && alertStatus.length > 0) {
    reqParams.status = alertStatus;
  }

  return useQuery<AlertCountsResponse, Error>({
    queryKey: ['alertCounts', lookBackStartDate, alertStatus, alertTypes, alertCategories],
    queryFn: () => fetchAlertCounts(reqParams),
  });
};
